/**
 * Auth Store Actions
 *
 * Contains all action implementations for the authentication store.
 *
 * @module @store/auth/authActions
 */

import type { AuthActions, AuthState } from './authTypes';

/**
 * Initial state reference for reset operations.
 * This is imported from store.ts to avoid circular dependencies.
 * We use a function to get the initial state to maintain the pattern.
 */
function getInitialState(): AuthState {
    return {
        isAuthenticated: false,
        accessToken: null,
    };
}

/**
 * Creates the auth actions object.
 *
 * @param set - Zustand set function
 * @param _initialState - Initial state (for potential future use)
 * @returns AuthActions object
 */
export function createAuthActions(
    set: (state: Partial<AuthState>) => void,
    _initialState: AuthState,
): AuthActions {
    return {
        /**
         * Sets the authentication state.
         * @param v - Whether the user is authenticated
         */
        setIsAuthenticated: (v: boolean): void => {
            set({ isAuthenticated: v });
        },

        /**
         * Sets the access token.
         * @param token - The access token to set, or null to clear
         */
        setAccessToken: (token: string | null): void => {
            set({ accessToken: token });
        },

        /**
         * Resets the auth state to initial values.
         */
        resetAuth: (): void => {
            set(getInitialState());
        },
    };
}
